<template>
	<div>
		<!-- Displayed value -->
		<form-field-readonly @click="openModal">
			{{ computedValue || "---" }}
		</form-field-readonly>

		<!-- Modal -->
		<core-modal :name="instanceKey" headline="Update Field">
			<div class="p4">
				<!-- Entity name-->
				<div class="mb6">
					{{ $t("update_field_of") }}
					<span class="fw7">{{ entityName }}:</span>
				</div>

				<!-- Form -->
				<div>
					<label for="value" class="block xs-12 lh125 mb1 fw7 fs3">
						{{ field.label }}
					</label>
					<!-- Normal component -->
					<core-form-input
						v-if="!hasCustomComponent"
						:componentName="fieldToUpdateComponent_.componentName"
						v-bind="fieldToUpdateComponent_.props"
						v-model="form.value"
						name="value"
					/>

					<!-- Custom injected component -->
					<component
						v-else
						:is="fieldToUpdateComponent_.componentName"
						v-bind="fieldToUpdateComponent_.props"
						v-model="form.value"
					/>
				</div>

				<!-- Button -->
				<div class="xs-12 mt4">
					<j-button :isLoading="isLoading" @click="updateEntity">
						{{ $t("save") }}
					</j-button>
				</div>
			</div>
		</core-modal>
	</div>
</template>

<script>
import CoreModal from "@/modules/core/components/CoreModal"
import JButton from "@/components/JButton"
import FormFieldReadonly from "../FormFieldReadonly.vue"
import CoreFormInput from "@/modules/core/components/CoreFormInput"

import updateIndividual from "../updateIndividual"
import updateCompany from "../updateCompany"
import updateObject from "../updateObject"
import updateParticipant from "../updateParticipant"
import updateOrganization from "../updateOrganization"
import { getFieldData } from "../utils/index.js"
import componentsDictionary from "../componentsDictionary.js"

const api = {
	updateIndividual,
	updateCompany,
	updateObject,
	updateParticipant,
	updateOrganization
}

export default {
	components: {
		CoreModal,
		JButton,
		FormFieldReadonly,
		CoreFormInput
	},

	props: {
		/**
		 * Value represents `id`
		 */
		value: {
			type: String
		},

		/**
		 * Field from schema
		 * It's an object that inlcudes numerous properties, read docs for more details
		 * We compute some values based on those properties
		 */
		field: {
			type: Object,
			default: () => ({})
		},

		/**
		 * Options for updates of field
		 * Includes component name and props see more in docs
		 */
		updateOptions: {
			type: Object,
			default: () => ({})
		}
	},

	data() {
		return {
			instanceKey: Math.random().toString(), // get unique key fro modal window

			isLoading: false,
			form: {
				value: ""
			}
		}
	},

	computed: {
		/**
		 * Determine whether it's normal component based on components dictionary
		 * or whether it is custom injected component
		 */
		hasCustomComponent() {
			return this?.updateOptions?.componentObject?.source ? true : false
		},

		/**
		 * It will run passed fieldToUpdateComponent prop through componentDictionary
		 */
		fieldToUpdateComponent_() {
			// 1: If we have custom injected component get it from `updateOptions.componentObject.source`
			if (this.hasCustomComponent) {
				const { source } = this?.updateOptions?.componentObject
				return {
					componentName: source,
					props: {
						...(this.updateOptions?.props || {})
					}
				}
			}

			// 2: If have normal component from components dictionary
			const { name, props } = componentsDictionary(
				this?.updateOptions?.component
			)

			return {
				componentName: name,
				props: {
					...props,
					...(this.updateOptions?.props || {})
				}
			}
		},

		/**
		 * Entity object that we get from API
		 * We get some other properties from this object that are crucial for some operations
		 * e.g. entityId, entityName, entityType via __typename
		 * thus it has to contain properties: `__typename`, `id` `name`
		 */
		entity() {
			const entity = getFieldData("entity", this.field, this)
			if (!entity)
				console.error(
					`There is no field.entity in ${this.field.label}, it will cause errors for adding and updating entity.`,
					this.field
				)
			return entity
		},

		/**
		 * Based on this we do API calls
		 */
		entityId() {
			return this.entity?.id
		},

		/**
		 * Based on this we do API calls
		 */
		entityType() {
			return this.entity?.__typename
		},

		/**
		 * When we need to display name of an entity
		 */
		entityName() {
			return this.entity?.name
		},

		computedValue() {
			return this?.entity?.[this.field.fieldToUpdate]
		}
	},

	methods: {
		// When user wants to open a modal to update field,
		// check if if we actually have `entity` to show `fieldToUpdate` for
		// If not, show message and don't show modal
		openModal() {
			if (!this.entity) {
				this.$store.dispatch("addMessage", {
					delay: 1500,
					message: {
						type: "neutral",
						text: "You have to select entity first"
					}
				})
				return
			}

			this.$modal.show(this.instanceKey)
		},

		async updateEntity() {
			this.isLoading = true

			// Set fieldToUpdate and normalize name and nameFormat
			let fieldToUpdate = this.field.fieldToUpdate
			if (fieldToUpdate === "name") fieldToUpdate = "nameFormat"
			if (!fieldToUpdate) console.error("There is no fieldToUpdate", this.field)

			// Variables
			const variables = {
				entityId: this.entityId,
				input: { [fieldToUpdate]: this.form.value }
			}

			// Decide query
			let query
			if (this.entityType === "individual") query = "updateIndividual"
			if (this.entityType === "company") query = "updateCompany"
			if (this.entityType === "object") query = "updateObject"
			if (this.entityType === "internal") query = "updateParticiapnt"
			if (this.entityType === "organization") query = "updateOrganization"

			// Call
			const res = await api[query](variables)

			// Errors
			if (res?.data?.[query]?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.[query]?.errors?.[0] || this.$t("errors.general")
				)
				this.isLoading = false
				return
			}

			// Success
			// Refetch case to update all possible entities in the graph with new data
			await this.$store.dispatch("caseManagement/fetchCase", {
				caseId: this.$store.state.caseManagement.caseId
			})

			// Stop loading
			this.isLoading = false
			this.$modal.hide(this.instanceKey)
		}
	},

	watch: {
		// When computedValue updates, update form
		computedValue: {
			handler() {
				this.form.value = this.computedValue
			},
			immediate: true,
			deep: true
		}
	}
}
</script>
